import React, { useState } from 'react';
import { Grid, makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Loader } from '@klp/design/Loader';

import { ActionLink, Block, Heading, Typography } from '@klp/design';
import type { LeverandorViewParams } from '../config';
import { LeverandorRoutes } from '../config';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { useTranslation } from 'react-i18next';

import { Main } from '@klp/layout';
import { LeverandorForm } from '../components/Form/LeverandorForm';
import { useGetLeverandorProcessByProcessIdQuery } from '../services/rtkApi';
import { useCurrentUser } from '@klp/auth';
import { leverandorSelectors, isActiveModal } from '../store/leverandorSlice';
import { TildelAnsvarlig } from '../components/TildelAnsvarlig/TildelAnsvarlig';
import { SendKommentar } from '../components/SendKommentar/SendKommentar';
import { StickyBox } from '@klp/design/StickyBox/StickyBox';
import { LeverandorStepper } from '../components/LeverandorStepper';
import { LeverandorActions } from '../components/LeverandorActions/LeverandorActions';
import { RolleKommentarList } from '../components/SendKommentar/RolleKommentarList';
import { showToast } from '@klpkonsern/ui';
import {
    activityIsHidden,
    convertFormFieldstoFormValues,
} from '@klp/formgenerator/utils';

const useStyles = makeStyles((theme) => ({
    notFoundContainer: {
        alignSelf: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '60%',
    },
    leverandorFormContainer: {
        flexGrow: 1,
        maxHeight: 'none',
        [theme.breakpoints.down('sm')]: {
            padding: theme.spacing(1),
        },
    },
    sidebarContainer: {
        display: 'flex',
        flexDirection: 'column' as const,
        maxHeight: `calc(100vh - var(--layout-top-offset) - ${theme.spacing(4)}px)`,
        gap: theme.spacing(1),
    },
    sidebarSectionActions: {
        flex: '0 0 auto',
        padding: theme.spacing(1),
    },
    sidebarSectionComments: {
        flex: '1 1 auto',
        overflowY: 'auto' as const,
        minHeight: 0,
        padding: theme.spacing(1),
    },
}));

export const LeverandorView = () => {
    const { t, i18n } = useTranslation();
    const classes = useStyles();
    const { prosessInstansId } = useParams<LeverandorViewParams>();
    const [activeStep, setActiveStep] = useState<number>();
    const { currentUser } = useCurrentUser();

    const { activeModal } = useSelector(leverandorSelectors);

    const {
        data: process,
        isLoading: processIsLoading,
        isFetching: processIsFetching,
        isError: processIsError,
    } = useGetLeverandorProcessByProcessIdQuery({
        prosessInstansId,
        culture: i18n.language,
    });

    if (processIsLoading || processIsFetching) {
        return <Loader isLoading={processIsLoading || processIsFetching} />;
    }

    if (processIsError || !process?.aktiviteter) {
        showToast({ body: t('general.error.message'), type: 'error' });

        return (
            <Main>
                <Block>
                    <ActionLink
                        actionType="linkBack"
                        translation="leverandor.backButton"
                        href={LeverandorRoutes.leverandorSchema()}
                    />
                </Block>
                <Block
                    className={classes.notFoundContainer}
                    paddingTop="massive"
                >
                    <Illustration
                        paddingBottom="compact"
                        icon={IconName.CIRCLE_OBS}
                        size="xxxlarge"
                    />
                    <Heading
                        klpHeadingLevel="TWO"
                        klpColor="dark"
                        translation="leverandor.notFound"
                        data-cy="Leverandor__NotFound__Title"
                    />
                </Block>
            </Main>
        );
    }

    const allFormValues = process.aktiviteter.reduce(
        (acc, aktivitet) => ({
            ...acc,
            ...convertFormFieldstoFormValues(aktivitet.formFields),
        }),
        {},
    );

    const visibleAktiviteter = process.aktiviteter.filter(
        (aktivitet) => !activityIsHidden(aktivitet, allFormValues),
    );

    return (
        <Main>
            <Main.Breadcrumbs>
                <Main.Crumb
                    id="leverandorSchema"
                    label="breadcrumbs.leverandor.schema"
                    href={LeverandorRoutes.leverandorSchema()}
                />
                <Main.Crumb id="leverandornavn" label={process.navn} />
            </Main.Breadcrumbs>
            <SendKommentar.Modal />
            {isActiveModal(activeModal, 'tildelAnsvarlig') &&
                process.currentAktivitet && (
                    <TildelAnsvarlig.Modal
                        superOfficeContactId={process.superOfficeContactId}
                        prosessInstansId={process.prosessInstansId}
                        kandidatGruppe={process.currentAktivitet.kandidatGruppe}
                        currentAnsvarlig={process.currentAktivitet.tildeltTil}
                        activity={process.currentAktivitet}
                    />
                )}
            <Main.Navigation>
                <ActionLink
                    actionType="linkBack"
                    translation="leverandor.backButton"
                    href={LeverandorRoutes.leverandorSchema()}
                />
            </Main.Navigation>

            <LeverandorStepper
                aktiviteter={visibleAktiviteter}
                processStatus={process.registreringStatus}
                callback={setActiveStep}
            />

            <Block
                padding="compact"
                className={classes.leverandorFormContainer}
            >
                {activeStep !== undefined && (
                    <Grid container={true} spacing={2}>
                        <Grid item={true} xs={12}>
                            <Typography variant="h2">
                                {t(
                                    `leverandor.createLeverandor.type.${process.type}`,
                                )}
                            </Typography>
                            <Typography variant="h1">
                                {`${process.navn} - ${process.orgnummer}`}
                            </Typography>
                        </Grid>

                        <Grid item={true} xs={12} md={9} lg={9}>
                            <LeverandorForm
                                prosessInstansId={process.prosessInstansId}
                                activity={visibleAktiviteter[activeStep]}
                                superofficeId={process.superOfficeContactId}
                                orgnummer={process.orgnummer}
                                registreringStatus={process.registreringStatus}
                                prosessRegion={process.region}
                            />
                        </Grid>
                        <Grid item={true} xs={12} md={3} lg={3}>
                            <StickyBox>
                                <div className={classes.sidebarContainer}>
                                    <div
                                        className={
                                            classes.sidebarSectionActions
                                        }
                                    >
                                        <LeverandorActions
                                            leverandor={process}
                                            isEDAdmin={currentUser?.isEDAdmin}
                                            navigateOnDelete={true}
                                            defaultRolle={
                                                activeStep !== undefined
                                                    ? visibleAktiviteter[
                                                          activeStep
                                                      ]?.kandidatGruppe
                                                    : undefined
                                            }
                                        />
                                    </div>
                                    <div
                                        className={
                                            classes.sidebarSectionComments
                                        }
                                    >
                                        <RolleKommentarList
                                            currentActivity={
                                                visibleAktiviteter[activeStep]
                                            }
                                            prosessInstansId={
                                                process.prosessInstansId
                                            }
                                            aktiviteter={process.aktiviteter}
                                        />
                                    </div>
                                </div>
                            </StickyBox>
                        </Grid>
                    </Grid>
                )}
            </Block>
        </Main>
    );
};
